import type { Constraints } from '@casa/layout';
import type { Alignment, GaugeLook, GaugeStyle, SparklinePlacement, Threshold, TileOf, UnitPosition } from '@casa/model';
import { formatNumber } from '@casa/util';
import { gradientColors } from '../../elements/color_gradient.ts';
import type { FontMetrics, TextStyle } from '../../elements/flex_label.ts';
import type { Sparkline } from '../../elements/sparkline.ts';
import type { IconDefinition } from '../../elements/icon.ts';
import { gaugeElement, groupElement, hangElement, iconElement, measuredOnly, sparklineElement, styledGaugeElement, textElement, type LayoutElement } from '../../elements/layout_elements.ts';
import { thresholdColor, type GaugeReading } from '../../elements/gauge_styles.ts';
import { transformValue } from '../../values/transform.ts';
import { TITLE_ROW_HEIGHT } from '../../view/theme.ts';

/**
 * The legacy `value` tile (`NumericValueTileView`): either a large fitted number with its unit at
 * the top right (`NumberComponentView`), or a gauge with the number in the middle, its unit below
 * it, an optional icon above it and an optional secondary value at the bottom (`GaugeComponentView`). The constraints are
 * the legacy ones, copied from the XAML and from NumericValueTileView.xaml.cs.
 */

export interface ValueGauge {
    value: number | null;
    min: number;
    max: number;
    colors: readonly string[];
    icon?: IconDefinition | undefined;
    iconColor: string;
    /** Formatted secondary value with its unit, for example `0,00 kWh`. */
    secondaryText?: string | undefined;
    secondaryColor: string;
    /** How it is drawn (docs/specs/gauge-styles.md); `ticks`, the legacy dial, when left out. */
    style?: GaugeStyle | undefined;
    /** The second value as a number, for the styles that show it on the scale. */
    secondaryValue?: number | null | undefined;
    /** Whether the second value is marked on the scale too (a set point is; another quantity is not). */
    marksSecond?: boolean | undefined;
    /** Values beyond the second (the rings), with their scales, colours and texts. */
    more?: readonly GaugeReading[] | undefined;
    thresholds?: readonly Threshold[] | undefined;
    coloring?: 'gradient' | 'threshold' | undefined;
    track?: string | undefined;
    look?: GaugeLook | undefined;
    locale?: string | undefined;
}

/** The colours along a dial by its thresholds: steps of them, for the legacy dial's gradient. */
export function thresholdSteps(thresholds: readonly Threshold[], min: number, max: number) : string[] {
    return Array.from({ length: 200 }, (_, index) => thresholdColor(thresholds, min + ((max - min) * (index + 0.5)) / 200) ?? '#ffffff');
}

/** What a gauge's options give the drawing, beside its values: its colouring and look. */
export function gaugeLookOf(gauge: { colors: string | readonly string[]; thresholds?: readonly Threshold[]; coloring?: 'gradient' | 'threshold' | 'single' | undefined; color?: string | undefined; track?: string | undefined; look?: GaugeLook | undefined; min: number; max: number }) : Pick<ValueGauge, 'colors' | 'thresholds' | 'coloring' | 'track' | 'look'> {
    const thresholds = gauge.thresholds ?? [];
    const coloring = gauge.coloring ?? (thresholds.length > 0 ? 'threshold' : gauge.color !== undefined ? 'single' : 'gradient');
    const gradient = gradientColors(gauge.colors);
    if (coloring === 'single') {
        // One colour (issue #23): one threshold over the whole scale, which every style draws in its colour
        // (the glow keeps its own colour in a gradient).
        const color = gauge.color ?? gradient[0] ?? '#ffffff';
        return { colors: [color, color], thresholds: [{ from: gauge.min, color }], coloring: 'threshold', track: gauge.track, look: gauge.look ?? {} };
    }

    return {
        // The legacy dial draws a gradient: by thresholds, their colours in steps.
        colors: coloring === 'threshold' && thresholds.length > 0 ? thresholdSteps(thresholds, gauge.min, gauge.max) : gradient,
        thresholds,
        coloring,
        track: gauge.track,
        look: gauge.look ?? {},
    };
}

export interface ValueTileContent {
    /** Formatted value, `-` when there is none. */
    numberText: string;
    unitText: string;
    /** The unit's box, and so its text, in parts of its usual size (the tile's `unitSize`, issue #6). */
    unitScale: number;
    /** Where the unit goes beside the number, without a gauge (TU-6, issue #25); at the top right when left out. */
    unitPosition?: UnitPosition | undefined;
    /** How the number and its unit line up across the area, without a gauge (TS-15); centred when left out. */
    align?: Alignment | undefined;
    color: string;
    gauge?: ValueGauge | undefined;
    /** The tile has a sparkline; below the value, its space is kept even while it has no data. */
    showSparkline?: boolean;
    /** Where the sparkline goes: in a strip below the value (when left out), or across the tile behind it (SP-11). */
    sparklinePlacement?: SparklinePlacement | undefined;
    /**
     * The line of text below the value (SR-8): kept, its room too, wherever the tile or one of its
     * rules has one, so that the value does not change size as rules start and stop holding; its text
     * then may be empty.
     */
    caption?: ValueCaption | undefined;
    /** Its height in percent of the tile (SP-12); the placement's default when left out. */
    sparklineHeight?: number | undefined;
    sparkline?: Sparkline | undefined;
}

/** How long a gauge takes to move to a new value (the owner, 2026-10-22). */
export const GAUGE_MOVE_MS = 500;

/** The values a gauge places on its scales, in order: the value, the second, the further ones; none without a gauge. */
export function gaugeValues(content: ValueTileContent) : (number | null)[] {
    const { gauge } = content;
    return gauge ? [gauge.value, gauge.secondaryValue ?? null, ...(gauge.more ?? []).map((reading) => reading.value)] : [];
}

/**
 * `content` with its gauge's values (`gaugeValues`' order) replaced by `values`, for drawing them
 * moving (docs/specs/gauge-styles.md §7). Only the places on the scales move; the texts stay.
 */
export function withGaugeValues(content: ValueTileContent, values: readonly (number | null)[]) : ValueTileContent {
    const { gauge } = content;
    if (!gauge) {
        return content;
    }

    const valueAt = (index: number, fallback: number | null) : number | null => (index < values.length ? (values[index] ?? null) : fallback);
    return {
        ...content,
        gauge: {
            ...gauge,
            value: valueAt(0, gauge.value),
            ...(gauge.secondaryValue === undefined ? {} : { secondaryValue: valueAt(1, gauge.secondaryValue) }),
            ...(gauge.more ? { more: gauge.more.map((reading, index) => ({ ...reading, value: valueAt(index + 2, reading.value) })) } : {}),
        },
    };
}

export interface TileFont {
    family: string;
    metrics: FontMetrics;
}

/** How high the sparkline is when the tile says nothing (SP-12): a fifth below the value, where the legacy NumericValueTileView had its graph; the whole tile behind it (SP-11). */
export const DEFAULT_SPARKLINE_HEIGHT: Readonly<Record<SparklinePlacement, number>> = { below: 20, behind: 100 };

/** The sparkline's area: at the bottom of the tile, as wide as it, `percent` of its height (SP-12, issue #29). */
export function sparklineArea(percent: number) : Constraints {
    return { ref: 'sparkline', bottom: 'parent.bottom', centerX: 'parent.center.X', h: `parent.height*${percent / 100}`, w: 'parent.width' };
}

/**
 * The part of the tile that the number or gauge component fills: above the sparkline, if there is one
 * below it, a twenty-fifth of the tile apart (the gap the legacy strip of a fifth had).
 */
function componentArea(showSparkline: boolean, hasCaption: boolean) : Constraints {
    return {
        centerX: 'parent.center.X',
        w: 'parent.width',
        bottom: hasCaption ? 'caption.top' : aboveSparkline(showSparkline),
        top: 'parent.top',
    };
}

/** The bottom of what is above a sparkline below the value, a twenty-fifth of the tile apart; else the tile's. */
const aboveSparkline = (showSparkline: boolean) : string => (showSparkline ? 'sparkline.top - parent.height/25' : 'parent.bottom');

export interface ValueCaption {
    text: string;
    color: string;
    /** Its text's height in pixels: a part of the title height (SR-9). */
    height: number;
    /** Lined up as the number when left out (SR-9). */
    align?: Alignment | undefined;
}

/** The caption's text height in parts of the title height, and its colour, where the style sets none (SR-9, the owner's). */
export const DEFAULT_CAPTION_SIZE = 0.6;
export const DEFAULT_CAPTION_COLOR = '#808080';

/** The caption's strip at most, in parts of the content area's height, so that a small tile keeps room for its value (SR-8). */
const CAPTION_MAX_HEIGHT = 0.25;

/**
 * The caption: a strip across the bottom of the area (above a sparkline below the value), its text
 * as high as the style says, or a quarter of the area where that is less, made smaller where it is
 * too long. Lined up as the style says, else as the number: centred under a gauge.
 */
function captionElement(content: ValueTileContent, caption: ValueCaption, showSparkline: boolean, font: TileFont) : LayoutElement {
    const align = caption.align ?? (content.gauge ? 'center' : content.align ?? 'center');
    const constraints = { ref: 'caption', left: 'parent.left', w: 'parent.width', bottom: aboveSparkline(showSparkline), h: `min(${caption.height}, parent.height*${CAPTION_MAX_HEIGHT})` };

    return textElement(constraints, caption.text, caption.color, { family: font.family, metrics: font.metrics, align, valign: 'center' });
}

function inkStyle(font: TileFont, options: Partial<TextStyle> = {}) : TextStyle {
    return { family: font.family, metrics: font.metrics, fill: true, ...options };
}

/** Between the number and a unit at its right, in parts of the number's height. */
const UNIT_GAP = 0.025;
/** Font size of a unit at most, in heights of its box: a `°` (about 0.3 em of ink) fills it, as a `'` (0.25 em) would. */
const UNIT_MAX_SIZE = 5;
/**
 * A number's descenders below its baseline, in parts of its height (`hang`, measured): those of a
 * comma, the deepest a number has, so that a number with one and without one are as large.
 */
const NUMBER_HANG = '(hang.height/100)';
/** A long number is squeezed to no less than this part of its width; beyond that it is made smaller (narrow tiles). */
const NUMBER_MIN_SQUEEZE = 0.6;
/**
 * The right of the number's ink, for each alignment (TS-15). Not `value.right`: the measured bounds
 * lie a side bearing to the right of the ink (the legacy render bounds), which took that much of the
 * unit's room. Centred, the number is on the area's middle; at the left, its ink starts at the
 * area's left; at the right, it ends where `unitRoom`, the unit's and its gap's, begins.
 */
function numberRight(align: Alignment, unitRoom: string) : string {
    switch (align) {
        case 'left':
            return 'parent.left + value.width';
        case 'center':
            return 'parent.center.X + value.width/2';
        case 'right':
            return `parent.right - ${unitRoom}`;
    }
}

/** Below the number, the unit lines up as it does: on the area's middle, or at its edge. */
const BELOW_ACROSS: Readonly<Record<Alignment, Constraints>> = {
    left: { left: 'parent.left' },
    center: { centerX: 'parent.center.X' },
    right: { right: 'parent.right' },
};

interface UnitBox {
    constraints: Constraints;
    align: Alignment;
    valign: 'top' | 'center' | 'bottom';
}

/**
 * The unit's box for each position (tiles spec TU-6, issue #25): a quarter of the number's height
 * times the unit's size, wherever it is, so that a long unit ("km/h") is as high as a short one
 * (the owner: "impossibly small" in the legacy box, 15 % of the number's width). At the right, level
 * with the number's top, middle or bottom, as wide as the room there; below it, lined up as the
 * number is, as wide as it.
 */
const UNIT_BOXES: Readonly<Record<UnitPosition, (scale: number, align: Alignment, right: string) => UnitBox>> = {
    'right-top': (scale, _align, right) => ({ constraints: { ref: 'unit', ...besideNumber(right), top: 'value.top', h: `value.height*0.25*${scale}` }, align: 'left', valign: 'top' }),
    'right-center': (scale, _align, right) => ({ constraints: { ref: 'unit', ...besideNumber(right), centerY: 'value.center.Y', h: `value.height*0.25*${scale}` }, align: 'left', valign: 'center' }),
    'right-bottom': (scale, _align, right) => ({ constraints: { ref: 'unit', ...besideNumber(right), bottom: 'value.bottom', h: `value.height*0.25*${scale}` }, align: 'left', valign: 'bottom' }),
    // Below there is the number's width to use: a quarter of its height, so that a long unit ("km/h") is
    // not fitted into the narrow box of the right (the owner: "impossibly small"). On the area's middle
    // or at its edge, as the number is: its measured bounds lie a side bearing to the right of its ink.
    // Under a comma's descender: the number's bounds end at its baseline.
    'below': (scale, align) => ({ constraints: { ref: 'unit', ...BELOW_ACROSS[align], top: `value.bottom + value.height*(${NUMBER_HANG} + 0.08)`, w: `value.width*${scale}`, h: `value.height*0.25*${scale}` }, align, valign: 'top' }),
};

/**
 * At the right: from just after the number (whose ink ends at `right`) to the area's edge, so that a
 * long or large unit is made smaller rather than cut off.
 */
function besideNumber(right: string) : Constraints {
    const left = `${right} + value.height*${UNIT_GAP}`;
    return { left, w: `max(1, parent.right - (${left}))` };
}

/**
 * Where the unit goes, for a position, the unit's size in parts of its usual one, and how the number
 * lines up (TS-15); at the right of a number at the right, `unitRoom` is the room left for it.
 */
export const unitBox = (position: UnitPosition, scale: number, align: Alignment = 'center', unitRoom = '0') : UnitBox => UNIT_BOXES[position](scale, align, numberRight(align, unitRoom));

function numberComponent(content: ValueTileContent, font: TileFont) : LayoutElement[] {
    const unitPosition = content.unitPosition ?? 'right-top';
    const align = content.align ?? 'center';
    const numberStyle = inkStyle(font, { align, valign: 'top', fitWidth: true, minScaleX: NUMBER_MIN_SQUEEZE });
    const hang = hangElement({ ref: 'hang', left: 'parent.left', top: 'parent.top', w: '1', h: '1' }, '0,', numberStyle);
    // A unit fills its box however little ink it has: a `°` as high as a quarter of the number.
    const unitText = (unit: UnitBox) : LayoutElement => textElement(unit.constraints, content.unitText, content.color, inkStyle(font, { align: unit.align, valign: unit.valign, maxSize: UNIT_MAX_SIZE }));

    if (unitPosition === 'below') {
        // Below, the number moves up and is a little smaller, to leave the unit room under it.
        const value = { ref: 'value', ...BELOW_ACROSS[align], centerY: 'parent.center.Y - parent.height*0.1', w: 'parent.width*0.6', h: 'parent.height*0.5' };
        return [hang, textElement(value, content.numberText, content.color, numberStyle), unitText(unitBox('below', content.unitScale, align))];
    }

    // At the right, the number's box leaves the unit and its gap beside it: on both sides when centred,
    // so that the number stays in the middle; on one side at the left or the right, the box then from
    // the area's left, the number lined up in it. The unit's size follows the number's height: both
    // texts are measured first in a box 100 high and as wide as they need (a fitted text scales with
    // its box). The number's height is then the box's, or less where the width left to it, at its
    // narrowest squeeze, limits it: worked out from the two texts' proportions. The box starts a
    // tenth of the area down and leaves a comma's descender room above the tenth at the bottom.
    const shape = (ref: string, text: string, style: TextStyle) : LayoutElement => measuredOnly(textElement({ ref, left: 'parent.left', top: 'parent.top', w: '1000000', h: '100' }, text, content.color, style));
    // Per pixel of the number's box height: the number's width at its narrowest, its ink's height, and the unit's and the gaps' width.
    const sides = align === 'center' ? 2 : 1;
    const squeeze = numberStyle.fitWidth === true ? numberStyle.minScaleX ?? 0 : 1;
    const numberWidth = `(numberShape.width/100*${squeeze})`;
    const numberHeight = '(numberShape.height/100)';
    const besideWidth = `${numberHeight}*(unitShape.width/100*${0.25 * sides * content.unitScale} + ${sides * UNIT_GAP})`;
    const numberBoxHeight = `parent.height*0.8/(1 + ${NUMBER_HANG})`;
    const boxHeight = `min(${numberBoxHeight}, parent.width/(${numberWidth} + ${besideWidth}))`;
    const unitRoom = `${boxHeight}*${besideWidth}`;
    const unit = unitBox(unitPosition, content.unitScale, align, unitRoom);
    const across: Constraints = align === 'center' ? { centerX: 'parent.center.X' } : { left: 'parent.left' };

    return [
        hang,
        shape('numberShape', content.numberText, numberStyle),
        shape('unitShape', content.unitText, inkStyle(font, { align: unit.align, valign: unit.valign, maxSize: UNIT_MAX_SIZE })),
        textElement({ ref: 'value', ...across, top: 'parent.top + parent.height*0.1', w: `parent.width - ${unitRoom}`, h: numberBoxHeight }, content.numberText, content.color, numberStyle),
        unitText(unit),
    ];
}

/** A gauge in one of the styles beside the legacy dial: one element that draws its texts too. */
function styledGaugeComponent(content: ValueTileContent, gauge: ValueGauge, font: TileFont) : LayoutElement[] {
    const readings: GaugeReading[] = [{ value: gauge.value, min: gauge.min, max: gauge.max }];
    const hasSecond = gauge.secondaryText !== undefined;
    if (hasSecond) {
        readings.push({ value: gauge.secondaryValue ?? null, min: gauge.min, max: gauge.max, color: gauge.secondaryColor, text: gauge.secondaryText });
    }
    readings.push(...(gauge.more ?? []));

    return [
        styledGaugeElement(
            { center: 'parent.center', w: 'parent.width', h: 'parent.height' },
            {
                style: gauge.style as Exclude<GaugeStyle, 'ticks'>,
                readings,
                hasSecond,
                marksSecond: gauge.marksSecond ?? gauge.look?.markSecond ?? false,
                numberText: content.numberText,
                unitText: content.unitText,
                unitScale: content.unitScale,
                color: content.color,
                icon: gauge.icon,
                iconColor: gauge.iconColor,
                colors: gauge.colors,
                thresholds: gauge.thresholds ?? [],
                coloring: gauge.coloring ?? 'gradient',
                track: gauge.track,
                look: gauge.look ?? {},
                locale: gauge.locale,
                font,
            },
        ),
    ];
}

function gaugeComponent(content: ValueTileContent, gauge: ValueGauge, font: TileFont) : LayoutElement[] {
    const numberStyle = inkStyle(font, { fitWidth: true, valign: 'bottom' });
    const elements: LayoutElement[] = [
        hangElement({ ref: 'hang', left: 'parent.left', top: 'parent.top', w: '1', h: '1' }, '0,', numberStyle),
        gaugeElement({ ref: 'gauge', center: 'parent.center', w: 'parent.width', h: 'parent.height' }, gauge),
        textElement(
            {
                ref: 'number',
                centerX: 'gauge.center.X',
                centerY: 'gauge.center.Y',
                h: 'gauge.height * 0.20',
                w: 'min(gauge.width/1.5,gauge.height/1.5)',
            },
            content.numberText,
            content.color,
            numberStyle,
        ),
        // The legacy view had the unit's label below the number, though its gauge never filled it in
        // (issue #4). Under a comma's descender: the number's bounds end at its baseline. A fifth of the
        // number's height apart, as in the other dials (the owner, 2026-10-28: a tenth was too tight).
        textElement(
            {
                ref: 'unit',
                top: `number.bottom + number.height*(${NUMBER_HANG} + 0.2)`,
                h: `gauge.height/8*${content.unitScale}`,
                centerX: 'parent.center.X',
                w: `gauge.width/2*${content.unitScale}`,
            },
            content.unitText,
            content.color,
            inkStyle(font, { valign: 'top' }),
        ),
        iconElement(
            {
                ref: 'icon',
                centerX: 'parent.center.X',
                centerY: 'gauge.top+gauge.height * 0.27',
                w: 'gauge.width*0.12',
                h: 'gauge.width*0.12',
            },
            gauge.icon,
            gauge.iconColor,
        ),
    ];

    if (gauge.secondaryText) {
        elements.push(
            textElement(
                {
                    ref: 'total',
                    centerX: 'gauge.center.X',
                    bottom: 'gauge.bottom',
                    h: 'gauge.height/12',
                    w: 'min(gauge.width/1.3,gauge.height/1.3)',
                },
                gauge.secondaryText,
                gauge.secondaryColor,
                inkStyle(font, { valign: 'bottom' }),
            ),
        );
    }

    return elements;
}

/** The elements of a value tile's content area. */
export function valueTileElements(content: ValueTileContent, font: TileFont) : LayoutElement[] {
    const { gauge } = content;
    const children = gauge ? (gauge.style && gauge.style !== 'ticks' ? styledGaugeComponent(content, gauge, font) : gaugeComponent(content, gauge, font)) : numberComponent(content, font);

    const showSparkline = content.showSparkline ?? false;
    const placement = content.sparklinePlacement ?? 'below';
    const below = showSparkline && placement === 'below';
    // Laid out before the value, which is placed above it.
    const caption = content.caption ? [captionElement(content, content.caption, below, font)] : [];
    const component = groupElement(componentArea(below, caption.length > 0), children);

    if (!showSparkline) {
        return [...caption, component];
    }

    // Drawn first, so that the value is drawn over it when it is behind.
    const area = sparklineArea(content.sparklineHeight ?? DEFAULT_SPARKLINE_HEIGHT[placement]);
    const sparkline = content.sparkline ? sparklineElement(area, content.sparkline) : groupElement(area, []);

    return [sparkline, ...caption, component];
}

// What a value tile shows, from its options and its values: the behaviour of the legacy
// `NumberComponent` and `GaugeComponent`.

export type ValueTile = TileOf<'value'>;

/** The colour of values that set none (legacy `White`). */
export const DEFAULT_VALUE_COLOR = '#ffffff';

/** What a value tile shows; `null` is no value (yet), drawn as "-". */
export interface ValueTileData {
    value: number | null;
    /** The gauge's secondary value, where the tile has one. */
    secondary?: number | null;
    /** The gauge's values beyond the second, in the order of its `more`. */
    more?: readonly (number | null)[];
}

export interface ValueTileOptions {
    icon?: IconDefinition | undefined;
    /** BCP 47 locale for numbers; the engine's default without it. */
    locale?: string | undefined;
    /** The tile's text colour (T-2), for what has no colour of its own. */
    textColor?: string | undefined;
    /** The tile style's gauge style (docs/specs/gauge-styles.md); `ticks` when left out. */
    gaugeStyle?: GaugeStyle | undefined;
    /** The tile style's value alignment (TS-15); centred when left out. */
    valueAlign?: Alignment | undefined;
    /** The caption of the style rules that hold (SR-8), over the tile's own. */
    caption?: string | undefined;
    /** The caption's text height in pixels (SR-9); 60 % of the default title height when left out. */
    captionHeight?: number | undefined;
    /** The tile style's caption colour (SR-9); grey when left out. */
    captionColor?: string | undefined;
    /** The tile style's caption alignment (SR-9); as the number when left out. */
    captionAlign?: Alignment | undefined;
}

/** A number in a .NET format, or "-" for none. */
export function formatTileNumber(value: number | null | undefined, numberFormat: string, locale: string | undefined) : string {
    return value === null || value === undefined ? '-' : formatNumber(value, numberFormat, locale);
}

export function valueTileContent(tile: ValueTile, sourceData: ValueTileData, options: ValueTileOptions = {}) : ValueTileContent {
    const tileOptions = tile.options;
    // The legacy number and gauge components transform the value before anything else. Units
    // are text only: a conversion is written in the transform (tiles spec TU-1 to TU-3).
    const data = { ...sourceData, value: transformValue(sourceData.value, tileOptions.transform) };
    const { locale } = options;
    const textColor = options.textColor ?? DEFAULT_VALUE_COLOR;
    const color = tileOptions.color ?? textColor;
    const { gauge } = tileOptions;
    const number = data.value;
    const content: ValueTileContent = {
        numberText: formatTileNumber(number, tileOptions.numberFormat, locale),
        // None while there is no number, as the legacy number component.
        unitText: number === null ? '' : tileOptions.unit,
        unitScale: tileOptions.unitSize / 100,
        unitPosition: tileOptions.unitPosition,
        align: options.valueAlign ?? 'center',
        color,
        showSparkline: tileOptions.sparkline !== undefined,
        sparklinePlacement: tileOptions.sparkline?.placement,
        sparklineHeight: tileOptions.sparkline?.height,
    };
    if (tileOptions.caption !== undefined || tileOptions.styleRules.some((rule) => rule.caption !== undefined)) {
        content.caption = {
            text: options.caption ?? tileOptions.caption ?? '',
            color: options.captionColor ?? DEFAULT_CAPTION_COLOR,
            height: options.captionHeight ?? TITLE_ROW_HEIGHT * DEFAULT_CAPTION_SIZE,
            align: options.captionAlign,
        };
    }

    if (!gauge) {
        return content;
    }

    const { secondary } = gauge;
    content.gauge = {
        value: data.value,
        min: gauge.min,
        max: gauge.max,
        icon: options.icon,
        iconColor: gauge.iconColor ?? textColor,
        secondaryText: secondary ? `${formatTileNumber(data.secondary, secondary.numberFormat, locale)}${secondary.unit}` : undefined,
        secondaryColor: secondary?.color ?? textColor,
        style: options.gaugeStyle ?? 'ticks',
        secondaryValue: data.secondary ?? null,
        more: gauge.more.map((more, index) => ({
            value: data.more?.[index] ?? null,
            min: more.min ?? gauge.min,
            max: more.max ?? gauge.max,
            color: more.color,
            text: `${formatTileNumber(data.more?.[index] ?? null, more.numberFormat, locale)}${more.unit}`,
        })),
        locale,
        ...gaugeLookOf(gauge),
    };

    return content;
}
